/**
 *
 * "Login page component. First page to view unauthenticated Users. Login from here by clicking the 'Sign In' buttons."
 *
 * @file   Landing.js
 * @author Lateral
 * @since  2023
 */
import React from 'react'
import { Auth } from 'aws-amplify'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import backgroundImage from 'assets/img/RAP_Artwork_Page_1.jpg'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DESKTOP_MIN_WIDTH } from 'common/config'
import { SandvikLogo } from 'components/SanvikLogo'
import { secondAccent } from 'mainTheme'
import { LoadingButton } from '@mui/lab'
import { useGetLegalDocuments } from 'hooks/useGetLegalDocuments'
import { saveAs } from 'file-saver'
import { Grid } from '@mui/material'
import { Loading } from 'components/Loading'

export function Landing() {
  /**
   * Generates the login page element
   * @Function
   *
   * @returns Generated paper element for login page
   */
  const isDesktop = useMediaQuery(DESKTOP_MIN_WIDTH)

  const onLoginPressed = () => {
    Auth.federatedSignIn()
  }

  const { data, query } = useGetLegalDocuments()

  return (
    <Paper
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        height: '100vh',
        width: '100vw'
      }}>
      {isDesktop ? (
        <Box display="flex" height="100%" width="100%" flexDirection="row-reverse">
          <Grid
            sx={{
              background: '#242424', // Black background
              display: 'flex',
              paddingLeft: '1.5em',
              paddingTop: '1em',
              paddingBottom: '1em'
            }}
            width="40%"
            height="100%"
            container
            justifyContent="space-between"
            flexDirection="column">
            <Box>
              <SandvikLogo className="landing" />
            </Box>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <Stack orientation="vertical" spacing={1}>
                <Typography variant="h2">Welcome</Typography>
                <Typography variant="body1">
                  Welcome to the Sandvik screening media deck management application. This application supports the
                  management of screens through providing a portal to record and track screen revisions and maintenance.
                </Typography>
                <Typography variant="body1">
                  Select the below and enter your account information to access the application and the deck database.
                </Typography>
              </Stack>

              <Box>
                <Typography variant="h2">Member Login</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ height: '40px', width: '209px' }}
                  onClick={onLoginPressed}>
                  Sign in
                </Button>
              </Box>

              <Box>
                <Typography variant="h4">Need Help?</Typography>
                <Typography>
                  Create a ticket
                  <a
                    target="blank"
                    style={{ color: secondAccent }}
                    href="https://rockprocessing-dsa.atlassian.net/servicedesk/customer/portal/34 ">
                    here
                  </a>
                </Typography>
              </Box>

              <Stack orientation="vertical" spacing={1}>
                <Typography variant="h4">{"Don't have an account?"}</Typography>
                <Typography variant="body1">
                  If your site already holds a subscription please reach out to your subscription manager to enable
                  access.
                </Typography>
                <Typography variant="body1">
                  If not, to organise a new subscription please contact the Sandvik sales team at:
                </Typography>
                <Typography>Tel: +61 (0) 2 9886 6800</Typography>
                <Typography>
                  Email:&nbsp;
                  <a
                    style={{ color: secondAccent }}
                    href={'mailto:DL_9853.ScreenMediaAM@sandvik.com?subject=DeckMapp Subscription Query'}>
                    Sales
                  </a>
                </Typography>
                <Grid
                  container
                  sx={{
                    marginTop: '1em'
                  }}>
                  {!query.isLoading ? (
                    query.isSuccess && (
                      <>
                        <Grid item marginRight={'1em'}>
                          <LoadingButton
                            color="primary"
                            size="small"
                            variant="contained"
                            loading={query.isLoading}
                            onClick={async () => {
                              saveAs(data?.eulDocument, 'DeckMapp EULA.pdf')
                            }}>
                            Download EUL
                          </LoadingButton>
                        </Grid>
                        <Grid item>
                          <LoadingButton
                            color="primary"
                            size="small"
                            variant="contained"
                            loading={query.isLoading}
                            onClick={async () => {
                              saveAs(data?.privacyPolicy, 'DeckMapp Privacy Policy.pdf')
                            }}>
                            Download Privacy Policy
                          </LoadingButton>
                        </Grid>
                      </>
                    )
                  ) : (
                    <Loading type="circular" withLogo={false} />
                  )}
                </Grid>
              </Stack>
            </Stack>

            <Box
              width="75%" // Adjust width as needed
              height="auto"
              p="auto"
              sx={{
                background: '#003C7D', // Background color
                borderRadius: '16px', // Rounded only on the top-left
                padding: '5px', // Adds spacing inside the box
                display: 'flex',
                alignItems: 'center', // Centers content vertically
                justifyContent: 'center' // Centers content horizontally
              }}>
              <Tooltip
                sx={{
                  width: '100%'
                }}
                title={
                  <React.Fragment>
                    <Typography variant="h5">JOURNEY ACROSS THE NATION - THROUGH TRADITIONAL TRADING ROUTES</Typography>
                    <Typography variant="body2">by Luke Penrith</Typography>
                    <Typography variant="body1">
                      {' '}
                      This artwork connects traditional means of trade of the past with our contemporary collaboration
                      through reconciliation and recognition of today.
                    </Typography>
                  </React.Fragment>
                }
                arrow>
                <Button>Learn More About This Artwork</Button>
              </Tooltip>
            </Box>
          </Grid>
        </Box>
      ) : (
        <Stack spacing={2} justifyContent="center" alignItems="center" height="100%">
          <Stack width="50%" alignItems="center">
            <Box
              width="65%"
              height="auto"
              p={0}
              sx={{ alignItems: 'center', background: '#242424', borderRadius: '16px' }}>
              <Stack sx={{ width: '165%', alignItems: 'left' }}>
                <SandvikLogo className="auth-logo" />
              </Stack>
            </Box>
          </Stack>

          <Box display="flex" flexDirection="column" justifyContent={'center'} alignItems={'center'}>
            <Box width="auto" height="auto" p={0} sx={{ background: '#242424', borderRadius: '16px' }}>
              <Typography variant="h2">Member Login</Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              sx={{ height: '40px', width: '209px' }}
              onClick={onLoginPressed}>
              auto Sign in
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  )
}
export default Landing
